import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import Button from '../components/UI/Button';
import AdoptionModal from '../components/Modals/AdoptionModal';
// import AdvancedSearch from '../components/Search/AdvancedSearch'; // Commented out for future use
import { adoptionsAPI } from '../services/api';

// Use the same interface as the modal for consistency
interface AdoptionApplication {
  id?: string;
  animalId: string;
  animalName: string;
  animalAge: string;
  animalGender: string;
  adopterFirstName: string;
  adopterLastName: string;
  adopterEmail: string;
  adopterPhone: string;
  adopterAddress: string;
  adopterCity: string;
  adopterCounty: string;
  adopterPostalCode: string;
  housingType: string;
  hasYard: boolean;
  yardSize: string;
  hasOtherPets: boolean;
  otherPetsDetails: string;
  hasChildren: boolean;
  childrenAges: string;
  previousPetExperience: string;
  adoptionReason: string;
  timeAvailable: string;
  applicant_references: Array<{
    name: string;
    phone: string;
    relationship: string;
  }>;
  applicationDate: string;
  status: 'pending' | 'approved' | 'rejected';
  homeVisitScheduled: boolean;
  homeVisitDate?: string;
  contractSigned: boolean;
  notes: string;
}

const Adoptions: React.FC = () => {
  const [applications, setApplications] = useState<AdoptionApplication[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<AdoptionApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false); // Commented out for future use
  const [selectedApplication, setSelectedApplication] = useState<AdoptionApplication | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [isSaving, setIsSaving] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(10); // Show 10 items per page
  const [stats, setStats] = useState<any>(null);

  // Load adoption applications from API with pagination
  const loadApplications = async (page: number = currentPage) => {
    try {
      setLoading(true);
      
      const params: any = {
        page: page,
        limit: itemsPerPage
      };
      
      // Add filters if they exist
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await adoptionsAPI.getAdoptions(params);
      if (response.success) {
        const { applications, pagination } = response.data;
        setApplications(applications || []);
        setFilteredApplications(applications || []);
        setTotalCount(pagination?.total || 0);
        setTotalPages(pagination?.pages || 1);
        setCurrentPage(page);
      } else {
        console.error('Failed to load adoption applications:', response.message);
        setApplications([]);
        setFilteredApplications([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading adoption applications:', error);
      setApplications([]);
      setFilteredApplications([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Load overall stats
  const loadStats = async () => {
    try {
      const response = await adoptionsAPI.getAdoptionsStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading adoptions stats:', error);
    }
  };

  useEffect(() => {
    loadApplications(1);
    loadStats();
  }, []);

  // Reload applications when status filter changes (not search term)
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filtering
    loadApplications(1);
  }, [statusFilter]);

  // Search function triggered by Enter key
  const handleSearch = () => {
    setCurrentPage(1);
    loadApplications(1);
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Since we're using server-side pagination and filtering,
  // filteredApplications is the same as applications from the server
  useEffect(() => {
    setFilteredApplications(applications);
  }, [applications]);

  // const handleAdvancedSearch = (filters: any) => {
  //   let filtered = applications;

  //   // Apply all the advanced filters
  //   if (filters.searchTerm) {
  //     filtered = filtered.filter(app =>
  //       app.animalName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
  //       app.adopterFirstName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
  //       app.adopterLastName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
  //       app.adopterEmail.toLowerCase().includes(filters.searchTerm.toLowerCase())
  //     );
  //   }

  //   if (filters.adoptionStatus) {
  //     filtered = filtered.filter(app => app.status === filters.adoptionStatus);
  //   }

  //   if (filters.adopterFirstName) {
  //     filtered = filtered.filter(app => 
  //       app.adopterFirstName.toLowerCase().includes(filters.adopterFirstName.toLowerCase())
  //     );
  //   }

  //   if (filters.adopterLastName) {
  //     filtered = filtered.filter(app => 
  //       app.adopterLastName.toLowerCase().includes(filters.adopterLastName.toLowerCase())
  //     );
  //   }

  //   if (filters.adopterEmail) {
  //     filtered = filtered.filter(app => 
  //       app.adopterEmail.toLowerCase().includes(filters.adopterEmail.toLowerCase())
  //     );
  //   }

  //   if (filters.hasHomeVisit !== null) {
  //     filtered = filtered.filter(app => 
  //       filters.hasHomeVisit ? !!app.homeVisitDate : !app.homeVisitDate
  //     );
  //   }

  //   if (filters.contractSigned !== null) {
  //     filtered = filtered.filter(app => app.contractSigned === filters.contractSigned);
  //   }

  //   if (filters.dateFrom) {
  //     filtered = filtered.filter(app => app.applicationDate >= filters.dateFrom);
  //   }

  //   if (filters.dateTo) {
  //     filtered = filtered.filter(app => app.applicationDate <= filters.dateTo);
  //   }

  //   setFilteredApplications(filtered);
  // }; // Commented out for future use

  const handleAddApplication = () => {
    setSelectedApplication(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  const handleEditApplication = (application: AdoptionApplication) => {
    setSelectedApplication(application);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleSaveApplication = async (applicationData: Partial<AdoptionApplication>) => {
    try {
      setIsSaving(true);
      if (modalMode === 'add') {
        const response = await adoptionsAPI.createAdoption(applicationData);
        if (response.success) {
          const newApplication = response.data;
          setApplications(prev => [...prev, newApplication]);
        } else {
          console.error('Failed to create adoption application:', response.message);
          alert('Eroare la crearea cererii de adopție');
        }
      } else {
        const response = await adoptionsAPI.updateAdoption(selectedApplication!.id!, applicationData);
        if (response.success) {
          const updatedApplication = response.data;
          setApplications(prev => prev.map(app => 
            app.id === selectedApplication?.id ? updatedApplication : app
          ));
        } else {
          console.error('Failed to update adoption application:', response.message);
          alert('Eroare la actualizarea cererii de adopție');
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving adoption application:', error);
      alert('Eroare la salvarea cererii de adopție');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (applicationId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      const response = await adoptionsAPI.updateStatus(applicationId, newStatus);
      if (response.success) {
        setApplications(prev => prev.map(app => 
          app.id === applicationId ? { ...app, status: newStatus } : app
        ));
      } else {
        console.error('Failed to update adoption status:', response.message);
        alert('Eroare la actualizarea statusului');
      }
    } catch (error) {
      console.error('Error updating adoption status:', error);
      alert('Eroare la actualizarea statusului');
    }
  };

  const handleDeleteApplication = async (application: AdoptionApplication) => {
    if (window.confirm(`Sigur doriți să ștergeți cererea pentru ${application.animalName}?`)) {
      try {
        await adoptionsAPI.deleteAdoption(application.id!);
        setApplications(prev => prev.filter(app => app.id !== application.id));
      } catch (error) {
        console.error('Error deleting adoption application:', error);
        alert('Eroare la ștergerea cererii de adopție');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'În așteptare', className: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Aprobată', className: 'bg-primary-100 text-primary-dark' },
      rejected: { label: 'Respinsă', className: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ro-RO');
  };

  // Statistics from overall data (not just current page)
  const totalApplications = stats?.total || totalCount;
  const pendingApplications = stats?.byStatus?.pending || 0;
  const approvedApplications = stats?.byStatus?.approved || 0;
  const rejectedApplications = stats?.byStatus?.rejected || 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Adopții</h1>
            <p className="text-gray-600">Gestionează cererile de adopție</p>
          </div>
          <div className="flex space-x-3">
            {/* <Button variant="outline" onClick={() => setIsAdvancedSearchOpen(true)}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Căutare avansată
            </Button> */}
            <Button variant="primary" onClick={handleAddApplication}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Cerere nouă
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-lg">
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total cereri</p>
                <p className="text-2xl font-bold text-gray-900">{totalApplications}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">În așteptare</p>
                <p className="text-2xl font-bold text-gray-900">{pendingApplications}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-lg">
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Aprobate</p>
                <p className="text-2xl font-bold text-gray-900">{approvedApplications}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Respinse</p>
                <p className="text-2xl font-bold text-gray-900">{rejectedApplications}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Căutare
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Animal, adoptator sau email... (apasă Enter)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  Caută
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Toate statusurile</option>
                <option value="pending">În așteptare</option>
                <option value="approved">Aprobate</option>
                <option value="rejected">Respinse</option>
              </select>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Animal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adoptator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data cererii
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      Nu au fost găsite cereri care să corespundă criteriilor de căutare.
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {application.animalName}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {application.animalId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {application.adopterFirstName} {application.adopterLastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {application.adopterEmail}
                        </div>
                        <div className="text-sm text-gray-500">
                          {application.adopterPhone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(application.applicationDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(application.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEditApplication(application)}
                            className="text-primary hover:text-primary-dark"
                          >
                            Editează
                          </button>
                          {application.status === 'pending' && (
                            <>
                              <button
                                onClick={() => application.id && handleStatusChange(application.id, 'approved')}
                                className="text-green-600 hover:text-green-900"
                              >
                                Aprobă
                              </button>
                              <button
                                onClick={() => application.id && handleStatusChange(application.id, 'rejected')}
                                className="text-red-600 hover:text-red-900"
                              >
                                Respinge
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDeleteApplication(application)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Șterge
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between sm:px-6 rounded-lg shadow">
            <div className="flex-1 flex items-center justify-between sm:hidden">
              <button
                onClick={() => loadApplications(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Anterior
              </button>
              <div className="flex items-center">
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                  {currentPage}/{totalPages}
                </span>
              </div>
              <button
                onClick={() => loadApplications(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Următor
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Afișare{' '}
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> la{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, totalCount)}
                  </span>{' '}
                  din <span className="font-medium">{totalCount}</span> cereri
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => loadApplications(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => loadApplications(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-primary text-white border-primary'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => loadApplications(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Adoption Modal */}
      <AdoptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveApplication}
        adoption={selectedApplication}
        mode={modalMode}
      />

      {/* Advanced Search Modal - Commented out for future use */}
      {/* <AdvancedSearch
        searchType="adoptions"
        isOpen={isAdvancedSearchOpen}
        onClose={() => setIsAdvancedSearchOpen(false)}
        onSearch={handleAdvancedSearch}
        onReset={() => setFilteredApplications(applications)}
      /> */}
    </Layout>
  );
};

export default Adoptions; 